import React from "react"
import { Top3User, UserQuota } from "../types/ai-dashboard"
import { AlertFilter } from "../_requests"
import MyTooltip from "@common-utils/MyTooltip"

interface Props {
     users: UserQuota[]
     top3: Top3User[]
     total: number
     perPage: number
     isLoading: boolean
     search: string
     page: number
     filter: AlertFilter
     startDate: string
     endDate: string
     onSearchChange: (value: string) => void
     onPageChange: (page: number) => void
     onFilterChange: (filter: AlertFilter) => void
     onDateChange: (start: string, end: string) => void
     onManageQuota: (user: UserQuota) => void
}

const currentMonthLabel = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date())

const UserManagement: React.FC<Props> = ({
     users,
     top3,
     total,
     perPage,
     isLoading,
     search,
     page,
     filter,
     startDate,
     endDate,
     onSearchChange,
     onPageChange,
     onFilterChange,
     onDateChange,
     onManageQuota,
}) => {
     const totalPages = Math.ceil(total / perPage)
     const top3Ids = new Set(top3.map(u => u.id))

     // Helper simplifié : Affiche 0 ou la valeur formatée
     const formatLimit = (limit: number | null) => {
          return limit?.toLocaleString() ?? "∞"
     }

     return (
          <div className="mt-5">
               {/* 1. BARRE DE FILTRES UNIFIÉE */}
               <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-3">
                         <div className="row g-3 align-items-center">
                              <div className="col-12 col-xl-4">
                                   <div className="position-relative">
                                        <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                        <input
                                             type="text"
                                             className="form-control ps-10 border-light bg-light"
                                             placeholder="Rechercher un utilisateur..."
                                             value={search}
                                             onChange={e => onSearchChange(e.target.value)}
                                        />
                                   </div>
                              </div>
                              <div className="col-12 col-md-6 col-xl-4">
                                   <div className="btn-group w-100 shadow-xs">
                                        {(["all", "daily", "monthly"] as AlertFilter[]).map(f => (
                                             <button
                                                  key={f}
                                                  className={`btn btn-sm fw-bold ${filter === f ? "btn-primary" : "btn-white border-light"}`}
                                                  onClick={() => onFilterChange(f)}
                                             >
                                                  {f === "all" ? "Tous" : f === "daily" ? "⚠️ Jour" : "🚨 Mois"}
                                             </button>
                                        ))}
                                   </div>
                              </div>
                              <div className="col-12 col-md-6 col-xl-4">
                                   <div className="d-flex align-items-center gap-2 bg-light rounded px-2 py-1 border border-light">
                                        <i className="bi bi-calendar-event text-primary"></i>
                                        <input
                                             type="date"
                                             className="form-control form-control-sm border-0 bg-transparent p-0"
                                             value={startDate}
                                             onChange={e => onDateChange(e.target.value, endDate)}
                                        />
                                        <span className="text-muted small">à</span>
                                        <input
                                             type="date"
                                             className="form-control form-control-sm border-0 bg-transparent p-0"
                                             value={endDate}
                                             onChange={e => onDateChange(startDate, e.target.value)}
                                        />
                                        {(startDate || endDate) && (
                                             <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => onDateChange("", "")}>
                                                  <i className="bi bi-x-circle-fill"></i>
                                             </button>
                                        )}
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               {/* PODIUM */}
               {!isLoading && top3.length > 0 && (
                    <div className="row g-4 mb-5 text-center align-items-end">
                         <div className="col-md-4 order-2 order-md-1">
                              <div className="card border-0 shadow-sm bg-white pb-3 h-100 justify-content-end border-bottom border-secondary border-3">
                                   <div className="display-6 text-secondary mb-2">🥈</div>
                                   <h5 className="fw-bold">{top3[1]?.name || "N/A"}</h5>
                                   <p className="small text-muted mb-0">{(top3[1]?.tokens || 0).toLocaleString()} tokens</p>
                              </div>
                         </div>
                         <div className="col-md-4 order-1 order-md-2">
                              <div className="card border-0 shadow-lg border-top border-warning border-5 pb-4 h-100 justify-content-end">
                                   <div className="display-4 mb-2">🥇</div>
                                   <h4 className="fw-bold text-primary">{top3[0]?.name || "N/A"}</h4>
                                   <p className="h5 fw-bold mb-0">{(top3[0]?.tokens || 0).toLocaleString()} tokens</p>
                              </div>
                         </div>
                         <div className="col-md-4 order-3">
                              <div className="card border-0 shadow-sm bg-white pb-3 h-100 justify-content-end border-bottom border-bronze border-3">
                                   <div className="display-6 mb-2" style={{ color: "#cd7f32" }}>
                                        🥉
                                   </div>
                                   <h5 className="fw-bold">{top3[2]?.name || "N/A"}</h5>
                                   <p className="small text-muted mb-0">{(top3[2]?.tokens || 0).toLocaleString()} tokens</p>
                              </div>
                         </div>
                    </div>
               )}

               {/* DATA DISPLAY */}
               <div className="card border-0 shadow-sm overflow-hidden">
                    <div className="table-responsive d-none d-lg-block">
                         <table className="table table-hover align-middle mb-0">
                              <thead className="table-light">
                                   <tr className="text-muted small text-uppercase fw-boldest">
                                        <th className="px-4">Utilisateur</th>
                                        <th>Consommation Période</th>
                                        <th className="text-center">Coût Période</th>
                                        <th>Quotas</th>
                                        <th className="text-end px-4">Actions</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {isLoading ? (
                                        <TableLoadingSkeleton perPage={perPage} />
                                   ) : users.length === 0 ? (
                                        <tr>
                                             <td colSpan={5} className="p-0">
                                                  <EmptyState />
                                             </td>
                                        </tr>
                                   ) : (
                                        users.map(user => <UserRow key={user.id} user={user} isTop3={top3Ids.has(user.id)} onManage={onManageQuota} formatLimit={formatLimit} />)
                                   )}
                              </tbody>
                         </table>
                    </div>

                    <div className="d-lg-none p-3">
                         {isLoading ? (
                              <div className="placeholder-glow">
                                   <div className="placeholder col-12 rounded vh-50"></div>
                              </div>
                         ) : users.length === 0 ? (
                              <EmptyState />
                         ) : (
                              users.map(user => <UserCard key={user.id} user={user} onManage={onManageQuota} formatLimit={formatLimit} />)
                         )}
                    </div>

                    <Pagination page={page} totalPages={totalPages} total={total} isLoading={isLoading} onPageChange={onPageChange} />
               </div>
          </div>
     )
}

// COMPOSANT DE STATUT ERGONOMIQUE
const QuotaStatusIndicator = ({ user, isCard = false }: { user: UserQuota; isCard?: boolean }) => {
     const dailyFull = user.dailyLimit !== null && user.dailyUsed >= user.dailyLimit
     const monthlyFull = user.monthlyLimit !== null && user.monthlyUsed >= user.monthlyLimit
     const dailyUnconfigured = user.dailyLimit === 0
     const monthlyUnconfigured = user.monthlyLimit === 0

     // Cas 1: Configuration manquante (Priorité haute)
     if (dailyUnconfigured || monthlyUnconfigured) {
          const msg = "Action requise : Configurez les quotas pour activer les fonctions IA."
          return isCard ? (
               <div className="alert alert-warning p-2 mini fw-bold text-center border-0 mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i> {msg}
               </div>
          ) : (
               <MyTooltip title={msg}>
                    <span className="badge bg-light-warning text-warning border border-warning border-opacity-25 mini fw-boldest uppercase mt-1">⚠️ Quotas à configurer</span>
               </MyTooltip>
          )
     }

     // Cas 2: Quota atteint
     if (dailyFull || monthlyFull) {
          const msg = dailyFull && monthlyFull ? "Limites journalière et mensuelle atteintes." : dailyFull ? "Limite journalière atteinte." : "Limite mensuelle atteinte."

          return isCard ? (
               <div className="alert alert-danger p-2 mini fw-bold text-center border-0 mb-3">
                    <i className="bi bi-slash-circle-fill me-2"></i> {msg}
               </div>
          ) : (
               <MyTooltip title={msg}>
                    <span className="badge bg-light-danger text-danger border border-danger border-opacity-25 mini fw-boldest uppercase mt-1">🚫 Limite atteinte</span>
               </MyTooltip>
          )
     }

     return null
}

const UserRow = ({ user, isTop3, onManage, formatLimit }: any) => {
     const dailyPct = user.dailyLimit ? (user.dailyUsed / user.dailyLimit) * 100 : 0
     const monthlyPct = user.monthlyLimit ? (user.monthlyUsed / user.monthlyLimit) * 100 : 0

     return (
          <tr className={isTop3 ? "table-warning table-opacity-10" : ""}>
               <td className="px-4">
                    {/* Affichage Nom + Username */}
                    <div className="fw-bold text-gray-800">{user.name}</div>
                    <div className="text-muted mini fs-8">{user.username}</div>
                    <QuotaStatusIndicator user={user} />
               </td>
               <td>
                    <div className="d-flex flex-wrap gap-1">
                         <Badge label="In" value={user.periodInput} color="primary" />
                         <Badge label="Cache" value={user.periodCached} color="success" />
                         <Badge label="Out" value={user.periodOutput} color="info" />
                    </div>
                    <div className="mt-1 mini text-muted">Total: {user.periodTotal.toLocaleString()}</div>
               </td>
               <td className="text-center fw-bold">${user.periodCost.toFixed(3)}</td>
               <td style={{ minWidth: "200px" }}>
                    <ProgressBar label="Aujourd'hui" used={user.dailyUsed} limit={user.dailyLimit} pct={dailyPct} formatLimit={formatLimit} />
                    <ProgressBar label={currentMonthLabel} used={user.monthlyUsed} limit={user.monthlyLimit} pct={monthlyPct} formatLimit={formatLimit} />
               </td>
               <td className="text-end px-4">
                    <button className="btn btn-icon btn-sm btn-light-primary" onClick={() => onManage(user)}>
                         <i className="bi bi-gear-fill"></i>
                    </button>
               </td>
          </tr>
     )
}

const UserCard = ({ user, onManage, formatLimit }: any) => {
     return (
          <div className="card border-light shadow-xs mb-3">
               <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                         <div>
                              <div className="fw-bold fs-6">{user.name}</div>
                              <div className="text-muted mini">ID: {user.id}</div>
                         </div>
                         <button className="btn btn-sm btn-icon btn-light-primary" onClick={() => onManage(user)}>
                              <i className="bi bi-gear-fill"></i>
                         </button>
                    </div>

                    <QuotaStatusIndicator user={user} isCard={true} />

                    <div className="row g-2 mb-3">
                         <div className="col-4 text-center border-end">
                              <div className="mini text-muted uppercase">Coût</div>
                              <div className="fw-bold">${user.periodCost.toFixed(2)}</div>
                         </div>
                         <div className="col-8 ps-3">
                              <div className="mini text-muted uppercase">Période</div>
                              <div className="fw-bold">{user.periodTotal.toLocaleString()} tokens</div>
                         </div>
                    </div>

                    <ProgressBar
                         label="Aujourd'hui"
                         used={user.dailyUsed}
                         limit={user.dailyLimit}
                         pct={user.dailyLimit ? (user.dailyUsed / user.dailyLimit) * 100 : 0}
                         formatLimit={formatLimit}
                    />
                    <ProgressBar
                         label="Ce mois"
                         used={user.monthlyUsed}
                         limit={user.monthlyLimit}
                         pct={user.monthlyLimit ? (user.monthlyUsed / user.monthlyLimit) * 100 : 0}
                         formatLimit={formatLimit}
                    />
               </div>
          </div>
     )
}

const Badge = ({ label, value, color }: any) => (
     <span className={`badge bg-light-${color} text-${color} border border-${color} border-opacity-10 px-2 py-1`}>
          <span className="mini opacity-75">{label}:</span> {value.toLocaleString()}
     </span>
)

const ProgressBar = ({ label, used, limit, pct, formatLimit }: any) => {
     const isUnconfigured = limit === 0
     return (
          <div className="mb-2">
               <div className="d-flex justify-content-between mini mb-1">
                    <span className="text-muted small">{label}</span>
                    <span className={isUnconfigured ? "text-warning fw-bold small" : "fw-bold"}>
                         {isUnconfigured ? "quota à configurer" : `${used.toLocaleString()} / ${formatLimit(limit)}`}
                    </span>
               </div>
               <div className="progress" style={{ height: "4px" }}>
                    <div
                         className={`progress-bar ${limit === 0 ? "bg-secondary opacity-50" : pct >= 100 ? "bg-danger" : pct > 80 ? "bg-warning" : "bg-primary"}`}
                         style={{ width: `${limit === 0 ? 0 : Math.min(100, pct)}%` }}
                    />
               </div>
          </div>
     )
}

const EmptyState = () => (
     <div className="text-center py-15">
          <div className="display-4 mb-2 opacity-25">🔍</div>
          <h5 className="text-muted fw-bold">Aucun utilisateur trouvé</h5>
          <p className="text-muted small">Essayez de modifier votre recherche ou vos filtres.</p>
     </div>
)

const TableLoadingSkeleton = ({ perPage }: any) => (
     <>
          {[...Array(perPage)].map((_, i) => (
               <tr key={i} className="placeholder-glow">
                    <td colSpan={5} className="px-4 py-4">
                         <div className="placeholder col-12 rounded opacity-25" style={{ height: "30px" }}></div>
                    </td>
               </tr>
          ))}
     </>
)

const Pagination = ({ page, totalPages, total, isLoading, onPageChange }: any) => {
     if (totalPages <= 1) return null
     return (
          <div className="card-footer bg-white border-top d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
               <span className="small text-muted">
                    {total} utilisateurs · page {page} / {totalPages}
               </span>
               <nav>
                    <ul className={`pagination pagination-sm mb-0 ${isLoading ? "opacity-50" : ""}`}>
                         <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => onPageChange(page - 1)} disabled={isLoading}>
                                   Précédent
                              </button>
                         </li>
                         <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => onPageChange(page + 1)} disabled={isLoading}>
                                   Suivant
                              </button>
                         </li>
                    </ul>
               </nav>
          </div>
     )
}

export default UserManagement
